import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';
import type { FilingCabinetItem } from '@/Models/FilingCabinet/FilingCabinet';
import { useTranslation } from 'react-i18next';
import Title from '@/Components/Title/Title';
import {
    DobAlert,
    DobButton,
    DobCheckbox, DobInput,
    DobSelect,
    DobSwitch,
    DobTab,
    DobTable,
    DobTableBody,
    DobTableDataCell,
    DobTableHeader,
    DobTableHeaderCell,
    DobTableRow,
    DobTabs,
} from '@dedalusdiit/dob-components';
import Styles from './PermissionsConfiguration.module.scss';
import { useFilingCabinetContext } from '@/Contexts/Authenticated/FilingCabinetContext';
import FilingCabinetStore from '@/Stores/FilingCabinetStore';
import PermissionsConfigurationStore, { PermissionsConfigurationTab } from '@/Stores/PermissionsConfigurationStore';
import { Dialog, DialogButtons, DialogContent, DialogTitle } from '@/Components/Dialog/Dialog';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import MenuItemPermissionsModel from '@/Models/PermissionsConfiguration/MenuItemPermissionsModel';
import RoleModel from '@/Models/PermissionsConfiguration/RoleModel';

interface PermissionsConfigurationProps {
    initialState: FilingCabinetItem;
}

interface UserGroupConfigurationProps {
    groupOptions: { label: string; value: number }[];
    store: PermissionsConfigurationStore;
}

interface GroupConfigurationProps {
    groupOptions: { label: string; value: number }[];
    store: PermissionsConfigurationStore;
}

interface RoleConfigurationProps {
    store: PermissionsConfigurationStore;
}

interface CreateUserDialogProps {
    groupOptions: { label: string; value: number }[];
    store: PermissionsConfigurationStore;
}

const PermissionsConfiguration = observer((props: Readonly<PermissionsConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();

    const fcContext = useFilingCabinetContext();
    const store = useMemo(() => new PermissionsConfigurationStore(new FilingCabinetStore(props.initialState, fcContext)), [props.initialState]);

    const groupOptions = useMemo(() => store.state.groups.map((x) => ({ label: x.Description, value: x.Id })), [store.state.groups]);

    return (
        <>
            <div className={Styles.pageContainer}>
                <Title titleName={t('UserManagement')} />
                <div className={Styles.contentContainer}>
                    <DobTabs
                        defaultTabKey="users"
                        valueTabKey={store.state.currentTab}
                        onChange={(tab) => store.setCurrentTab(tab as PermissionsConfigurationTab)}
                        className={Styles.flex}
                    >
                        <DobTab trigger={t('Users')} internalKey="users" className={Styles.mainTab + ' ' + Styles.flex}>
                            <UserGroupConfiguration
                                groupOptions={groupOptions}
                                store={store}
                            />
                        </DobTab>
                        <DobTab trigger={t('Groups')} internalKey="groups" className={Styles.mainTab}>
                            <GroupConfiguration groupOptions={groupOptions} store={store} />
                        </DobTab>
                        <DobTab trigger={t('Roles')} internalKey="roles" className={Styles.mainTab}>
                            <RoleConfiguration store={store} />
                        </DobTab>
                    </DobTabs>
                </div>
            </div>

            <CreateUserDialog store={store} groupOptions={groupOptions} />
            <CreateGroupDialog store={store} groupOptions={groupOptions} />
            <DeleteGroupDialog store={store} groupOptions={groupOptions} />
            <NewRoleDialog store={store} />
            <DeleteRoleDialog store={store} />
        </>
    );
});

const UserGroupConfiguration = observer((props: Readonly<UserGroupConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { groupOptions, store } = props;

    return (
        <div className={Styles.tabContent}>
            <div className={Styles.userFilterOptions}>
                <DobInput
                    label={t('Search')}
                    onValueChange={(x) => store.setUserFilter(x)}
                    value={store.state.userFilter}
                    placeholder={t('Name')}
                />
                <DobCheckbox label={t('ShowInactiveUsers')} checked={store.state.showInactiveUsers} onCheckedChange={(x) => store.setShowInactiveUsers(x as boolean)} />
            </div>
            <br />
            <DobTable>
                <DobTableHeader>
                    <DobTableRow>
                        <DobTableHeaderCell>Name</DobTableHeaderCell>
                        <DobTableHeaderCell>Group</DobTableHeaderCell>
                        <DobTableHeaderCell>Active</DobTableHeaderCell>
                    </DobTableRow>
                </DobTableHeader>
                <DobTableBody>
                    {store.allUsers.map((u) => {
                        if (!store.state.showInactiveUsers && !u.Active) {
                            return '';
                        }

                        return (
                            <DobTableRow key={u.Id}>
                                <DobTableDataCell>
                                    <Link className={Styles.userNameLink} to={`/UserDefaults/${u.Id}`} title={t('UserDefaults')}>
                                        #{u.Id} - <b>{u.Name}</b> ({u.Username})
                                    </Link>
                                </DobTableDataCell>
                                <DobTableDataCell className={Styles.userListGroupOption}>
                                    <div>
                                        <DobSelect
                                            size="small"
                                            optionSelected={groupOptions.find((x) => x.value == u.GroupId)}
                                            options={groupOptions}
                                            onChange={(opt) => {
                                                if (opt) {
                                                    store.setGroupOption(u.Id, opt.value);
                                                }
                                            }}
                                        />
                                        <DobButton
                                            color="neutral"
                                            icon="pencil-square"
                                            title={t('GoToGroup')}
                                            onClick={() => store.goToGroup(u.GroupId)}
                                        />
                                    </div>
                                </DobTableDataCell>
                                <DobTableDataCell className={!u.Active ? Styles.inactive : ''} style={{ width: '10%' }}>
                                    <DobCheckbox
                                        checked={u.Active}
                                        label={u.Active ? t('Active') : t('NotActive')}
                                        onCheckedChange={(checked) => store.setUserActive(u.Id, checked as boolean)}
                                    />
                                </DobTableDataCell>
                            </DobTableRow>
                        );
                    })}
                </DobTableBody>
            </DobTable>
            <div className={Styles.actions}>
                <DobButton
                    color="secondary"
                    onClick={() => store.setCreateUserDialogOpen(true)}
                >
                    {t('AddUser')}
                </DobButton>
            </div>
        </div>
    );
});

const CreateUserDialog = observer((props: Readonly<CreateUserDialogProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { store, groupOptions } = props;

    const templateUserOptions = store.templateUsers.map((x) => ({ value: x.Id, label: x.Username }));

    return (
        <Dialog open={store.createUserDialogOpen}>
            <DialogTitle>{t('AddUser')}</DialogTitle>
            <DialogContent>
                <div className={Styles.createUserContent}>
                    <div>
                        <DobInput
                            isRequired
                            direction="vertical"
                            label={t('Username')}
                            error={store.newUserErrors?.username != null}
                            value={store.newUserData.username}
                            onValueChange={(x) => store.setNewUserUsername(x)}
                        />
                        { store.newUserErrors?.username && <DobAlert type="error">{store.newUserErrors.username[0]}</DobAlert> }
                    </div>
                    <div>
                        <DobInput
                            isRequired
                            direction="vertical"
                            label={t('Name')}
                            error={store.newUserErrors?.name != null}
                            value={store.newUserData.name}
                            onValueChange={(x) => store.setNewUserName(x)}
                        />
                        { store.newUserErrors?.name && <DobAlert type="error">{store.newUserErrors.name[0]}</DobAlert> }
                    </div>
                    <DobInput
                        isRequired
                        direction="vertical"
                        label={t('Password')}
                        type="password"
                        value={store.newUserData.password}
                        onValueChange={(x) => store.setNewUserPassword(x)}
                    />
                    <div>
                        <DobInput
                            isRequired
                            direction="vertical"
                            label={t('RepeatPassword')}
                            type="password"
                            error={store.newUserErrors?.repeatPassword != null}
                            value={store.newUserData.repeatPassword}
                            onValueChange={(x) => store.setNewUserRepeatPassword(x)}
                        />
                        { store.newUserErrors?.repeatPassword && <DobAlert type="error">{store.newUserErrors.repeatPassword[0]}</DobAlert> }
                    </div>
                    <div>
                        <DobSelect
                            required
                            menuPosition="absolute"
                            direction="vertical"
                            label={t('Group')}
                            error={store.newUserErrors?.group != null}
                            optionSelected={groupOptions.find((x) => x.value == store.newUserData.group)}
                            options={groupOptions}
                            onChange={(x) => {
                                if (x) {
                                    store.setNewUserGroup(x.value);
                                }
                            }}
                        />
                        { store.newUserErrors?.group && <DobAlert type="error">{store.newUserErrors.group[0]}</DobAlert> }
                    </div>
                    <DobSelect
                        menuPosition="absolute"
                        direction="vertical"
                        label={t('TemplateUser')}
                        optionSelected={templateUserOptions.find((x) => x.value == store.newUserData.templateUser)}
                        options={templateUserOptions}
                        onChange={(x) => {
                            if (x) {
                                store.setNewUserTemplate(x.value);
                            }
                        }}
                    />
                </div>
                <br />
                { store.newUserServerError
                    ? (
                            <DobAlert type="error">
                                {store.newUserServerError}
                            </DobAlert>
                        )
                    : <></>}
            </DialogContent>
            <DialogButtons>
                <DobButton
                    color="secondary"
                    onClick={() => store.setCreateUserDialogOpen(false)}
                >
                    {t('Cancel')}
                </DobButton>
                <DobButton
                    onClick={() => store.createUser()}
                >
                    {t('Add')}
                </DobButton>
            </DialogButtons>
        </Dialog>
    );
});

const GroupConfiguration = observer((props: Readonly<GroupConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { store, groupOptions } = props;

    const doNavigate = (url: string): void => {
        navigate(url)
            .catch((err) => console.log(err));
    };

    return (
        <div className={Styles.tabContent}>
            <div className={Styles.groupContent}>
                <DobSelect
                    label={t('Group')}
                    direction="vertical"
                    options={groupOptions}
                    optionSelected={groupOptions.find((x) => x.value == store.state.selectedGroupId)}
                    onChange={(selected) => {
                        if (selected) {
                            store.setSelectedGroup(selected.value);
                        }
                    }}
                />
                <br />
                <div>
                    <div className={Styles.header}>{t('AssignedUsers')} ({store.usersInGroup.length})</div>
                    <br />
                    <div>
                        <DobInput
                            label={t('Search')}
                            onValueChange={(val) => store.setAssignedUserFilter(val)}
                            value={store.state.assignedUserFilter}
                            placeholder={t('Name')}
                            disabled={store.selectedGroup === undefined}
                        />
                    </div>
                    <br />
                    <div className={Styles.assignedUsers}>
                        {store.usersInGroup.map((x) => (<div key={x.Id}><DobButton color="silent" onClick={() => doNavigate(`/UserDefaults/${x.Id}`)}>{x.Name}</DobButton></div>))}
                    </div>
                </div>
                <br />
                <div>
                    <div className={Styles.header}>{t('AssignedRoles')}</div>
                    <div className={Styles.assignedRoles}>
                        {store.state.roles.map((x) => (
                            <div key={x.Id} className={Styles.role}>
                                <DobSwitch
                                    key={x.Id}
                                    label={x.Name}
                                    checked={store.selectedGroup?.Roles.includes(x.Id)}
                                    onChange={(checked) => store.toggleGroupRole(x.Id, checked)}
                                    disabled={store.selectedGroup === undefined}
                                />
                                <DobButton
                                    color="neutral"
                                    icon="pencil-square"
                                    size="small"
                                    title={t('Edit')}
                                    onClick={() => store.goToRole(x.Id)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
            <div className={Styles.actions}>
                <DobButton
                    color="danger"
                    onClick={() => store.setDeleteCurrentGroupDialogOpen(true)}
                    disabled={store.selectedGroup === undefined}
                >
                    {t('DeleteGroup')}
                </DobButton>
                <DobButton
                    color="secondary"
                    onClick={() => store.setCreateGroupDialogOpen(true)}
                >
                    {t('CreateNewGroup')}
                </DobButton>
            </div>
        </div>
    );
});

const CreateGroupDialog = observer((props: Readonly<GroupConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { store } = props;

    return (
        <Dialog open={store.createGroupDialogOpen}>
            <DialogTitle>{t('NewGroup')}</DialogTitle>
            <DialogContent>
                <DobInput
                    label={t('Name')}
                    value={store.newGroupName}
                    onValueChange={(val) => store.setNewGroupName(val)}
                />
            </DialogContent>
            <DialogButtons>
                <DobButton
                    color="secondary"
                    onClick={() => store.setCreateGroupDialogOpen(false)}
                >
                    {t('Cancel')}
                </DobButton>
                <DobButton
                    onClick={() => store.createNewGroup()}
                >
                    {t('Add')}
                </DobButton>
            </DialogButtons>
        </Dialog>
    );
});

const DeleteGroupDialog = observer((props: Readonly<GroupConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { store } = props;

    return (
        <Dialog open={store.deleteCurrentGroupDialogOpen}>
            <DialogTitle>{t('DeleteGroup')}</DialogTitle>
            <DialogContent>
                <span>
                    { store.deleteError != null && <div>{store.deleteError}</div> }
                    { store.deleteError == null && <div>{t('AreYouSure')}</div> }
                </span>
            </DialogContent>
            <DialogButtons>
                <DobButton
                    color="secondary"
                    onClick={() => store.setDeleteCurrentGroupDialogOpen(false)}
                >
                    {t('Cancel')}
                </DobButton>
                <DobButton
                    color="danger"
                    onClick={() => store.deleteSelectedGroup()}
                >
                    {t('Delete')}
                </DobButton>
            </DialogButtons>
        </Dialog>
    );
});

const RoleConfiguration = observer((props: Readonly<RoleConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { store } = props;

    const roles = useMemo(() => store.state.roles.map((x) => ({ label: x.Name, value: x.Id })), [store.state.roles]);
    const selectedRole = useMemo(() => store.selectedRole, [store.state.selectedRole]);

    return (
        <div className={Styles.tabContent}>
            <DobSelect
                label={t('Role')}
                direction="vertical"
                optionSelected={roles.find((x) => x.value == store.state.selectedRole)}
                options={roles}
                onChange={(option) => {
                    if (option) {
                        store.setSelectedRole(option.value);
                    }
                }}
            />
            <br />
            <div className={Styles.roleContent}>
                <div className={Styles.roleColumns}>
                    <div className={Styles.header}>{t('Permissions')}</div>
                    { store.state.permissions.map((m) => (
                        <PermissionItem
                            key={m.Id}
                            item={m}
                            store={store}
                            selectedRole={selectedRole}
                        />
                    ))}

                    <div className={Styles.header}>{t('MenuItems')}</div>
                    { store.state.menuItems.map((m) => (
                        <MenuItem
                            key={m.Id}
                            item={m}
                            store={store}
                        />
                    ))}
                </div>
            </div>
            <div className={Styles.actions}>
                <DobButton
                    color="danger"
                    onClick={() => store.setDeleteCurrentRoleDialogOpen(true)}
                    disabled={store.selectedRole == undefined}
                >
                    {t('DeleteRole')}
                </DobButton>
                <DobButton
                    color="secondary"
                    onClick={() => store.setCreateNewRoleDialogOpen(true)}
                >
                    {t('CreateNewRole')}
                </DobButton>
            </div>
        </div>
    );
});

const MenuItem = observer(({ item, store }: Readonly<{ item: MenuItemPermissionsModel; store: PermissionsConfigurationStore }>): React.ReactElement => {
    const { t } = useTranslation();

    const classes = [
        Styles.menuItem,
        item.ParentId ? Styles.child : '',
    ].join(' ');
    return (
        <div key={item.Id} className={classes}>
            <div>
                {t(item.Text)}
            </div>
            <div>
                <DobSwitch
                    onChange={() => store.toggleEnabledMenuItem(item.Id)}
                    checked={store.isMenuItemEnabled(item.Id)}
                    disabled={store.selectedRole == undefined}
                />
            </div>
        </div>
    );
});

const PermissionItem = observer(({ item, store, selectedRole }: Readonly<{ item: MenuItemPermissionsModel; store: PermissionsConfigurationStore; selectedRole: RoleModel | undefined }>): React.ReactElement => {
    const { t } = useTranslation();
    return (
        <div key={item.Id} className={Styles.permissionItem}>
            <div>
                {t(item.Text)}
            </div>
            <div>
                <DobSwitch
                    checked={selectedRole?.EnabledPermissionIds.includes(item.Id)}
                    onChange={(checked) => store.toggleRolePermission(item.Id, checked)}
                    disabled={store.selectedRole == undefined}
                />
            </div>
        </div>
    );
});

const NewRoleDialog = observer((props: Readonly<RoleConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { store } = props;

    return (
        <Dialog open={store.createNewRoleDialogOpen}>
            <DialogTitle>{t('NewRole')}</DialogTitle>
            <DialogContent>
                <DobInput
                    label={t('Name')}
                    value={store.newRoleName}
                    onValueChange={(val) => store.setNewRoleName(val)}
                />
            </DialogContent>
            <DialogButtons>
                <DobButton
                    color="secondary"
                    onClick={() => store.setCreateNewRoleDialogOpen(false)}
                >
                    {t('Cancel')}
                </DobButton>
                <DobButton
                    onClick={() => store.createNewRole()}
                >
                    {t('Add')}
                </DobButton>
            </DialogButtons>
        </Dialog>
    );
});

const DeleteRoleDialog = observer((props: Readonly<RoleConfigurationProps>): React.ReactElement => {
    const { t } = useTranslation();
    const { store } = props;

    return (
        <Dialog open={store.deleteCurrentRoleDialogOpen}>
            <DialogTitle>{t('DeleteRole')}</DialogTitle>
            <DialogContent>
                <span>{t('DeleteRoleQuestion')}</span>
                {store.roleDeleteError
                    ? (
                            <>
                                <br />
                                <DobAlert type="error">{store.roleDeleteError}</DobAlert>
                            </>
                        )
                    : <></>}
            </DialogContent>
            <DialogButtons>
                <DobButton
                    color="secondary"
                    onClick={() => store.setDeleteCurrentRoleDialogOpen(false)}
                >
                    {t('Cancel')}
                </DobButton>
                <DobButton
                    color="danger"
                    onClick={() => store.deleteSelectedRole()}
                >
                    {t('Delete')}
                </DobButton>
            </DialogButtons>
        </Dialog>
    );
});

export default PermissionsConfiguration;
